import { useEffect, useId, useState, type ReactNode } from "react";
import type { FilingStatus, TraceStep } from "@ftr/tax-core";
import { parseMoney } from "../lib/fields.ts";
import { traceValue } from "../lib/format.ts";

export function MoneyField(props: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  hint?: string;
  optional?: boolean;
  allowNegative?: boolean;
}) {
  const id = useId();
  const [touched, setTouched] = useState(false);
  const parsed = parseMoney(props.value);
  const negativeNotAllowed = !props.allowNegative && parsed.kind === "value" && parsed.value.lessThan(0);
  const error = parsed.kind === "error" ? parsed.message : negativeNotAllowed ? "This can't be negative" : null;
  return (
    <div className={"field" + (touched && error ? " field--error" : "")}>
      <label htmlFor={id}>
        {props.label}
        {props.optional && <span className="optional">optional</span>}
      </label>
      <div className="money-input">
        <span aria-hidden="true">$</span>
        <input
          id={id}
          inputMode="decimal"
          autoComplete="off"
          placeholder={props.optional ? "0" : ""}
          value={props.value}
          onChange={(e) => props.onChange(e.target.value)}
          onBlur={() => {
            setTouched(true);
            // Tidy a valid amount into 12,500.00 style so what's on screen matches what's counted.
            if (parsed.kind === "value" && !negativeNotAllowed) props.onChange(groupDigits(props.value));
          }}
          aria-invalid={Boolean(touched && error)}
        />
      </div>
      {touched && error ? <p className="field-msg">{error}</p> : props.hint ? <p className="field-hint">{props.hint}</p> : null}
    </div>
  );
}

const STATUSES: { value: FilingStatus; label: string }[] = [
  { value: "single", label: "Single" },
  { value: "mfj", label: "Married filing jointly" },
  { value: "mfs", label: "Married filing separately" },
  { value: "hoh", label: "Head of household" },
  { value: "qss", label: "Qualifying surviving spouse" },
];

export function StatusField({ value, onChange }: { value: FilingStatus; onChange: (v: FilingStatus) => void }) {
  const id = useId();
  return (
    <div className="field">
      <label htmlFor={id}>
        Filing status
      </label>
      <select id={id} value={value} onChange={(e) => onChange(e.target.value as FilingStatus)}>
        {STATUSES.map((s) => (
          <option key={s.value} value={s.value}>
            {s.label}
          </option>
        ))}
      </select>
    </div>
  );
}

export function Choice<T extends string>(props: {
  legend: string;
  value: T | "";
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
  hint?: ReactNode;
}) {
  const name = useId();
  return (
    <fieldset className="choice">
      <legend>{props.legend}</legend>
      {props.hint && <p className="field-hint">{props.hint}</p>}
      <div className="choice-row">
        {props.options.map((o) => (
          <label key={o.value} className={"chip" + (props.value === o.value ? " chip--on" : "")}>
            <input type="radio" name={name} value={o.value} checked={props.value === o.value} onChange={() => props.onChange(o.value)} />
            {o.label}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

export function Card({ title, children, className }: { title?: string; children: ReactNode; className?: string }) {
  return (
    <section className={"card " + (className ?? "")}>
      {title && <h2 className="card-title">{title}</h2>}
      {children}
    </section>
  );
}

/** Groups inputs under a small heading, like a section of a tax form. */
export function Section({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="form-section">
      <p className="form-section-label">{label}</p>
      {children}
    </div>
  );
}

export function WhyPanel({ trace, title = "Why this amount?" }: { trace: TraceStep[]; title?: string }) {
  return (
    <details className="why">
      <summary>{title}</summary>
      <ol>
        {trace.map((t, i) => (
          <li key={i}>
            <span className="why-label">
              {t.label}
              {t.rule && <span className="why-rule">{t.rule}</span>}
            </span>
            <span className="why-value">{traceValue(t.value)}</span>
          </li>
        ))}
      </ol>
    </details>
  );
}

export function NotIncluded({ items, assumedZero, assumptions }: { items: string[]; assumedZero?: string[]; assumptions?: string[] }) {
  return (
    <details className="fine">
      <summary>What this estimate assumes</summary>
      <ul>
        <li>Federal tax for 2026 only, using final IRS figures (Rev. Proc. 2025-32). State tax isn't included yet.</li>
        {assumptions?.map((a) => (
          <li key={a}>Assumes: {a}.</li>
        ))}
        {assumedZero && assumedZero.length > 0 && <li>Left blank, so counted as $0: {assumedZero.join(", ")}.</li>}
        {items.map((i) => (
          <li key={i}>Not included: {i}.</li>
        ))}
      </ul>
    </details>
  );
}

export function Pending({ missing, errors }: { missing: string[]; errors: string[] }) {
  return (
    <div className="pending">
      {errors.length > 0 && <p>Check these amounts: {errors.join(", ")}. Use a format like 12,500, with no minus sign unless the field says a loss is allowed.</p>}
      {missing.length > 0 && <p>Fill in {missing.join(", ")} to see your numbers.</p>}
    </div>
  );
}

/** A horizontal stacked bar. Segments with zero or negative values are skipped. */
export function StackBar({ parts }: { parts: { label: string; value: number; tone: string }[] }) {
  const shown = parts.filter((p) => p.value > 0);
  const total = shown.reduce((a, b) => a + b.value, 0);
  if (total <= 0) return null;
  return (
    <div className="stack">
      <div className="stack-bar" role="img" aria-label={shown.map((p) => p.label).join(", ")}>
        {shown.map((p) => (
          <span key={p.label} className={"seg seg--" + p.tone} style={{ flexGrow: p.value }} />
        ))}
      </div>
      <ul className="stack-legend">
        {shown.map((p) => (
          <li key={p.label}>
            <span className={"dot seg--" + p.tone} />
            {p.label}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Adds thousands separators to a valid amount as typed, keeping its sign, cents and parentheses. */
function groupDigits(raw: string): string {
  const m = /^(\(?-?\$?)(\d+(?:,\d{3})*)(\.\d+)?(\)?)$/.exec(raw.trim());
  if (!m) return raw;
  const int = m[2]!.replace(/,/g, "").replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return (m[1] ?? "").replace("$", "") + int + (m[3] ?? "") + (m[4] ?? "");
}

/** On phones the result sits below the form; this pill shows the headline and jumps to it. */
export function JumpToResult({ text }: { text: string }) {
  const [resultVisible, setResultVisible] = useState(false);
  useEffect(() => {
    const el = document.getElementById("result");
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(([e]) => setResultVisible(Boolean(e?.isIntersecting)), { threshold: 0.05 });
    io.observe(el);
    return () => io.disconnect();
  });
  if (resultVisible) return null;
  return (
    <a className="jump" href="#result" onClick={(e) => { e.preventDefault(); document.getElementById("result")?.scrollIntoView({ behavior: "smooth" }); }}>
      <span>{text}</span>
      <span aria-hidden="true">↓</span>
    </a>
  );
}
